import { Content as _Content } from '#~/common/editor/index.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { ReactElement } from 'react';
import { resolveSelectorHeading, shouldShowPreviewColumn } from '../morph-state.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/types.js';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { getVendorContent } from '@studio/design-system/themes/vendor.js';
import { ALERT_ACTION, createAlertTracker } from '#~/analytics/domains/alert-lifecycle.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { ONBOARDING_ADD_METHOD } from '#~/analytics/constants/onboarding-events.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useAgents } from '#~/agent/hooks.js';
import { limitCanAddCamera } from '#~/api/billing/limits.js';
import { Sel, STagNew, DataSTag } from '#~/stag/index.js';
import { applyManualRtspReset, clearManualRtspSeeds } from './utils.js';
import { BridgeSelector } from './bridge-selector.jsx';
import { Discovery } from './discovery.jsx';
import { Selector, useBulkDiscardConfirmation } from './selector.jsx';
import { useMultiSelectState } from './use-multi-select-state.js';

/**
 * Source `<Content>` component.
 *
 * When `bulkMode === true` the layout morphs to render only the selector
 * full-width — the right column (preview/bridge selector) is suppressed
 * because the per-selection preview is bypassed in bulk mode (see
 * mediator.tsx guard).
 *
 * ENG-4485: there is no Single/Multiple selector. `bulkMode` is DERIVED
 * from the selection (true iff ≥1 checkbox ticked), so the morph fires on
 * the FIRST tick and reverts on the LAST untick. Which control the user
 * touches decides the mode — a checkbox enters bulk, a row click (with an
 * empty selection) enters single-add.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the morph
 * contract is centralized in `morph-state.ts` so all three morph-aware
 * components (Scaffold / Content / Footer) read from the same helper.
 *
 * Per `think-in-systems` (ai.knowledge/global): the morph is layout-only —
 * no business logic crosses between the two branches, keeping the bulk
 * path composable without coupling to the single-add preview state graph.
 *
 * `useMultiSelectState()` is owned HERE (not in `<Selector>`) so the
 * reducer state lives above the `<Selector>` subtree and can never be lost
 * to a Selector remount; the controller is threaded into `<Selector>` as a
 * prop so there is exactly one reducer instance.
 *
 * Bug fix (Multi-Add Phase 1 manual QA): the prior implementation
 * conditionally rendered `<Selector />` under TWO different parent trees
 * (`<Columns><div>...` vs `<div>...`) depending on `bulkMode`. React
 * reconciles by tree position — when `bulkMode` flipped, the Selector
 * was unmounted and remounted. Fix: keep `<Selector />` at a single,
 * stable tree position (`_Content > div > div > Selector`) and toggle
 * the second column in/out as a SIBLING. The grid layout class on the
 * outer wrapper switches with `showPreview` but the wrapper itself is the
 * same React node, so reconciliation preserves the subtree across the
 * morph. The grid className itself is conditional rather than the parent
 * component type. Below the full dialog width plus its 64px viewport margin,
 * stack the preview below discovery so neither column is squeezed or clipped.
 */
export const Content = (props: HtmlAttributes): ReactElement => {
  const warning = useStore((state) => state.discoveryWarning);
  const roomId = useStore((state) => state.roomId);
  const producer = useProducer();
  const { track } = useAnalytics();
  // Match the footer: embedded cameras cannot host a manual RTSP source.
  const hasManualBridge = useAgents((agents) => agents.some((agent) => !agent.embedded));
  // Read the derived bulkMode from the store (mirrored from the reducer by
  // <Selector>). ENG-4485: bulkMode is true iff the selection is non-empty —
  // the single-add layout (preview column shown) holds while nothing is
  // checked, and morphs to full-width on the first tick.
  const bulkMode = useStore(({ source }) => source.bulkMode);

  // Own the multi-select controller here (see component doc). Passed down
  // to <Selector> so there is a single reducer instance / single writer.
  const multi = useMultiSelectState();
  const { tryDiscard } = useBulkDiscardConfirmation();

  // Pure-function morph decisions; tested in `morph-state.test.ts`.
  const showPreview = shouldShowPreviewColumn(bulkMode);
  const heading = resolveSelectorHeading(bulkMode);

  return (
    <_Content {...props}>
      {/* Outer wrapper: same React node in both modes — only its className
          (grid vs single-column) changes. This keeps the Selector subtree
          at a stable position so React preserves it across the morph. */}
      <div className={showPreview ? 'grid grid-cols-1 gap-24 min-[1264px]:grid-cols-2' : undefined}>
        {/* Left/main column: source selector. Position is invariant across
            the morph — renders at `_Content > div > div > Selector` whether
            bulkMode is on or off. */}
        <div>
          {/* The discovery heading is just the title; ENG-4485 removed the
              Single/Multiple toggle that used to sit in the card's top bar.
              `resolveSelectorHeading` already returns the single-add heading
              when preview is shown (bulkMode false), so `heading` alone covers
              both modes — no need to branch on `showPreview`.
              Per `reference-rules-in-comments`. */}
          <h3>{heading}</h3>
          {warning && (
            <Callout
              severity='warning'
              role='status'
              title='Some cameras might be missing'
              className='mb-12'
              {...DataSTag(Sel.AddVideoDeviceDiscovery, 'warning')}
            >
              <p className='mt-8 text-sm text-muted'>If yours is missing, add it by its address.</p>
              <div className='mt-12 flex items-center gap-12'>
                {hasManualBridge && (
                  <Button
                    size='sm'
                    variant='tertiary'
                    className='focus-visible:ring-primary/50 focus-visible:ring-2'
                    disabled={!limitCanAddCamera()}
                    {...STagNew(Sel.AddVideoDeviceDiscovery, 'add_by_address')}
                    onClick={() => {
                      if (!hasManualBridge || !limitCanAddCamera()) return;
                      createAlertTracker(track).trackAlertActed(warning, {
                        action: ALERT_ACTION.ADD_BY_ADDRESS
                      });
                      tryDiscard(() => {
                        track(EVENT_NAMES.DEVICE_CLICK_MANUAL_ADD, EVENT_CATEGORIES.DEVICE, {
                          studio_id: roomId
                        });
                        multi.deselectAll();
                        producer((draft) => {
                          // Leave bulk mode before opening the single-camera form;
                          // its mediator otherwise skips manual-entry resolution.
                          draft.source.bulkMode = false;
                          draft.source.multiSelection = [];
                          draft.addMethod = ONBOARDING_ADD_METHOD.MANUAL;
                          applyManualRtspReset(draft.candidate);
                          clearManualRtspSeeds(draft.source);
                          draft.panel = Panel.Video;
                        });
                      });
                    }}
                  >
                    Add by address
                  </Button>
                )}
                <a
                  // Final discovery articles are a pre-ship dependency owned by
                  // Content. Keep the existing product help centers usable meanwhile.
                  href={getVendorContent({
                    iris: 'https://intercom.help/iris-ai/en',
                    ptzoptics: 'https://hivehelp.ptzoptics.com/en'
                  })}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='focus-visible:ring-primary/50 text-sm text-primary focus-visible:ring-2'
                  {...STagNew(Sel.AddVideoDeviceDiscovery, 'learn_more')}
                  onClick={() =>
                    createAlertTracker(track).trackAlertActed(warning, {
                      action: ALERT_ACTION.LEARN_MORE
                    })
                  }
                >
                  Learn more
                </a>
              </div>
            </Callout>
          )}
          <Selector multi={multi} />
        </div>

        {/* Right column: video preview + bridge selection. Only rendered
            in single-add layout. Its presence/absence as a sibling does
            not move the Selector subtree above. */}
        {showPreview && (
          <div>
            <h3>Video Preview</h3>
            <Discovery />
            <BridgeSelector />
          </div>
        )}
      </div>
    </_Content>
  );
};
